import * as THREE from 'three';
import { RailPath } from './RailPath.js';
import {
  darkMetalMaterial,
  emissiveMaterial,
  glassMaterial,
  paintedMetalMaterial,
  rubberMaterial,
} from '../core/MaterialLibrary.js';

export class Vehicle {
  readonly mesh: THREE.Group;
  readonly cameraMount: THREE.Object3D;
  /** Fixed physical mounting point for the compact in-car VR instruments. */
  readonly dashboardAnchor: THREE.Object3D;
  readonly steeringWheel: THREE.Group;

  // Hood visible from cockpit
  private hoodGroup: THREE.Group;
  private exteriorGroup = new THREE.Group();
  // Exterior windshield — must never block the first-person view
  private exteriorGlass: THREE.Mesh | null = null;

  // Interior windscreen that cracks up as damage accumulates
  private glassCtx: CanvasRenderingContext2D | null = null;
  private glassTexture: THREE.CanvasTexture | null = null;
  private damageTier = 0;

  // Smooth steering visual
  private steeringAngle: number = 0;
  private bankAngle: number = 0;
  private rollQuat = new THREE.Quaternion();
  private forwardAxis = new THREE.Vector3(0, 0, 1);

  // Left-hand-drive seat positions (eye height, slightly behind the dash)
  private static readonly DRIVER_SEAT = new THREE.Vector3(-0.45, 1.44, 0.2);
  private static readonly PASSENGER_SEAT = new THREE.Vector3(0.5, 1.44, 0.2);
  // camera sits a touch above the cockpit anchor — drops the whole car
  // lower in the frame without squashing the dash layout
  private static readonly EYE_LIFT = 0.08;

  constructor() {
    this.mesh = new THREE.Group();
    this.mesh.add(this.exteriorGroup);

    // Camera mount — starts in the driver seat; setSeat() moves it per role
    this.cameraMount = new THREE.Object3D();
    this.cameraMount.position.copy(Vehicle.DRIVER_SEAT);
    this.cameraMount.position.y += Vehicle.EYE_LIFT;
    this.mesh.add(this.cameraMount);

    // Cockpit is fixed to the CAR at the driver seat (not the camera), so the
    // gunner sees the wheel over on the left where it belongs.
    const cockpitAnchor = new THREE.Object3D();
    cockpitAnchor.position.copy(Vehicle.DRIVER_SEAT);
    this.mesh.add(cockpitAnchor);

    // Centre the shared readout low in the dash, below the road sightline.
    // It belongs to the car rather than the player's head, so looking away
    // behaves exactly like looking away from a real instrument cluster.
    this.dashboardAnchor = new THREE.Object3D();
    this.dashboardAnchor.position.set(0.52, -0.39, -0.79);
    cockpitAnchor.add(this.dashboardAnchor);

    this.hoodGroup = this.createCockpit();
    cockpitAnchor.add(this.hoodGroup);

    this.steeringWheel = this.createSteeringWheel();
    cockpitAnchor.add(this.steeringWheel);

    // External car body (visible to other players / third person)
    this.createExterior();
  }

  /** Put the local player's eyes (and VR rig) in their seat. */
  setSeat(isDriver: boolean): void {
    this.cameraMount.position.copy(isDriver ? Vehicle.DRIVER_SEAT : Vehicle.PASSENGER_SEAT);
    this.cameraMount.position.y += Vehicle.EYE_LIFT;
  }

  private createCockpit(): THREE.Group {
    const cockpit = new THREE.Group();
    cockpit.position.y = -0.155; // keep the interior to a slim band at the bottom
    // the cockpit group is anchored at the DRIVER seat; car centerline is right of it
    const carX = 0.45;

    // interior cabin lamp — keeps everything readable in every theme
    const cabinLamp = new THREE.PointLight(0xffd8ec, 3, 3.4, 1.6);
    cabinLamp.position.set(0.45, 0.3, -0.9);
    cockpit.add(cabinLamp);

    // OutRun-style layout: thin top lip + short hood, nothing below — the
    // road owns the screen, not the dashboard.
    const lip = new THREE.Mesh(
      new THREE.BoxGeometry(2.3, 0.025, 0.32),
      darkMetalMaterial(0x151922, 0.5)
    );
    lip.position.set(carX, -0.275, -0.92);
    cockpit.add(lip);

    // interior windscreen: transparent canvas that accumulates cracks
    const glassCanvas = document.createElement('canvas');
    glassCanvas.width = 1024;
    glassCanvas.height = 400;
    this.glassCtx = glassCanvas.getContext('2d')!;
    this.glassTexture = new THREE.CanvasTexture(glassCanvas);
    const windscreen = new THREE.Mesh(
      new THREE.PlaneGeometry(2.4, 1.05),
      new THREE.MeshBasicMaterial({
        map: this.glassTexture,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
    );
    windscreen.position.set(carX, 0.08, -0.98);
    windscreen.rotation.x = -0.3; // raked like the exterior glass
    windscreen.renderOrder = 5;
    cockpit.add(windscreen);

    // scuttle: angled panel from the lip's near edge down toward the seats,
    // so you never see raw road sliding beneath the dash (jarring in VR)
    const scuttle = new THREE.Mesh(
      new THREE.PlaneGeometry(2.6, 0.85),
      new THREE.MeshStandardMaterial({ color: 0x10131a, roughness: 0.68, metalness: 0.18, side: THREE.DoubleSide })
    );
    scuttle.position.set(carX, -0.55, -0.52);
    scuttle.rotation.x = -1.05; // leans back toward the occupants
    cockpit.add(scuttle);

    // teal edge line where the lip meets the scuttle
    const lipEdge = new THREE.Mesh(
      new THREE.BoxGeometry(2.3, 0.018, 0.02),
      emissiveMaterial(0x20eaf0, 2.8)
    );
    lipEdge.position.set(carX, -0.278, -0.75);
    cockpit.add(lipEdge);

    // KITT-style neon line along the lip's front edge
    const dashLine = new THREE.Mesh(
      new THREE.BoxGeometry(2.2, 0.018, 0.02),
      emissiveMaterial(0x20eaf0, 2.8)
    );
    dashLine.position.set(carX, -0.262, -0.77);
    cockpit.add(dashLine);

    // ---- digital gauge pod behind the wheel (all self-lit) ----
    const cluster = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 0.15, 0.05),
      new THREE.MeshBasicMaterial({ color: 0x0a0c12 })
    );
    cluster.position.set(0, -0.185, -0.88);
    cockpit.add(cluster);
    const clusterEdge = new THREE.Mesh(
      new THREE.BoxGeometry(0.82, 0.012, 0.02),
      new THREE.MeshBasicMaterial({ color: 0xff2d95 })
    );
    clusterEdge.position.set(0, -0.255, -0.86);
    cockpit.add(clusterEdge);

    // twin glowing gauges + needles
    const gaugeFace = new THREE.MeshBasicMaterial({ color: 0x10131c });
    for (const [x, ringColor, needleAngle] of [
      [-0.14, 0x2ee6d6, 0.7],
      [0.14, 0xff2d95, -0.5],
    ] as Array<[number, number, number]>) {
      const face = new THREE.Mesh(new THREE.CircleGeometry(0.055, 16), gaugeFace);
      face.position.set(x, -0.175, -0.852);
      cockpit.add(face);

      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.05, 0.062, 16),
        new THREE.MeshBasicMaterial({ color: ringColor })
      );
      ring.position.set(x, -0.175, -0.851);
      cockpit.add(ring);

      const needleGeo = new THREE.BoxGeometry(0.007, 0.045, 0.004);
      needleGeo.translate(0, 0.0225, 0); // pivot at the gauge center
      const needle = new THREE.Mesh(needleGeo, new THREE.MeshBasicMaterial({ color: 0xffe066 }));
      needle.position.set(x, -0.175, -0.849);
      needle.rotation.z = needleAngle;
      cockpit.add(needle);
    }

    // center segment bar (fake speed readout)
    const segColors = [0x2ee6d6, 0x2ee6d6, 0x2ee6d6, 0xffe066, 0xffe066, 0xff9a3a, 0xff2d95];
    segColors.forEach((color, i) => {
      const seg = new THREE.Mesh(
        new THREE.BoxGeometry(0.016, 0.016 + i * 0.003, 0.004),
        new THREE.MeshBasicMaterial({ color })
      );
      seg.position.set(-0.075 + i * 0.025, -0.238, -0.85);
      cockpit.add(seg);
    });

    // center console with glowing switchgear, over toward the car centerline
    const console_ = new THREE.Mesh(
      new THREE.BoxGeometry(0.34, 0.34, 0.08),
      new THREE.MeshLambertMaterial({ color: 0x1d2029 })
    );
    console_.position.set(carX + 0.02, -0.5, -0.62);
    console_.rotation.x = -0.5;
    cockpit.add(console_);
    for (let i = 0; i < 6; i++) {
      const btnColors = [0xff2d95, 0x2ee6d6, 0xffe066, 0x2ee6d6, 0xff9a3a, 0x66ff41];
      const btn = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.035, 0.01),
        new THREE.MeshBasicMaterial({ color: btnColors[i] })
      );
      btn.position.set(carX - 0.08 + (i % 3) * 0.09, -0.44 - Math.floor(i / 3) * 0.09, -0.63 + Math.floor(i / 3) * 0.045);
      btn.rotation.x = -0.5;
      cockpit.add(btn);
    }

    // short hood sloping away from the lip — its tip rises just into view
    // (unlit flat tone: Lambert goes pitch black on real GPUs at night)
    const hoodMat = new THREE.MeshStandardMaterial({
      color: 0x0c1017,
      roughness: 0.88,
      metalness: 0.12,
    });
    const hoodSlope = new THREE.Mesh(new THREE.BoxGeometry(2.05, 0.04, 1.0), hoodMat);
    hoodSlope.position.set(carX, -0.36, -1.55);
    hoodSlope.rotation.x = 0.1;
    cockpit.add(hoodSlope);

    // teal cowl line across the hood's top edge
    const cowl = new THREE.Mesh(
      new THREE.BoxGeometry(2.05, 0.02, 0.03),
      emissiveMaterial(0x20eaf0, 2.8)
    );
    cowl.position.set(carX, -0.315, -1.07);
    cockpit.add(cowl);

    // neon edge strips so the hood corners read
    const stripMat = emissiveMaterial(0xff168c, 3.1);
    for (const side of [-1, 1]) {
      const strip = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.045, 1.0), stripMat);
      strip.position.set(carX + side * 0.95, -0.335, -1.45);
      strip.rotation.x = 0.1;
      cockpit.add(strip);
    }

    return cockpit;
  }

  private createSteeringWheel(): THREE.Group {
    const wheelGroup = new THREE.Group();
    wheelGroup.position.set(0, -0.30, -0.58);

    // Wheel rim (torus)
    const rimGeo = new THREE.TorusGeometry(0.12, 0.022, 8, 24);
    const rimMat = new THREE.MeshBasicMaterial({ color: 0x3a3f4d });
    const rim = new THREE.Mesh(rimGeo, rimMat);
    wheelGroup.add(rim);

    // side grip pads
    for (const side of [-1, 1]) {
      const grip = new THREE.Mesh(
        new THREE.BoxGeometry(0.036, 0.11, 0.04),
        new THREE.MeshBasicMaterial({ color: 0x2ee6d6 })
      );
      grip.position.set(side * 0.13, 0, 0.025);
      wheelGroup.add(grip);
    }

    // glowing top-center marker stripe (racing style)
    const marker = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, 0.026, 0.014),
      new THREE.MeshBasicMaterial({ color: 0xff2d95 })
    );
    marker.position.set(0, 0.118, 0);
    wheelGroup.add(marker);

    // Spokes (3-spoke like a sports car)
    const spokeMat = new THREE.MeshBasicMaterial({ color: 0x272b34 });
    for (let i = 0; i < 3; i++) {
      const angle = (i * Math.PI * 2) / 3 - Math.PI / 2;
      const spokeGeo = new THREE.CylinderGeometry(0.014, 0.014, 0.11, 4);
      const spoke = new THREE.Mesh(spokeGeo, spokeMat);
      spoke.position.set(Math.cos(angle) * 0.056, Math.sin(angle) * 0.056, 0);
      spoke.rotation.z = angle + Math.PI / 2;
      wheelGroup.add(spoke);
    }

    // Center hub with glowing core
    const hubGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.015, 12);
    const hubMat = new THREE.MeshBasicMaterial({ color: 0x30343f });
    const hub = new THREE.Mesh(hubGeo, hubMat);
    hub.rotation.x = Math.PI / 2;
    wheelGroup.add(hub);
    const hubGlow = new THREE.Mesh(
      new THREE.CircleGeometry(0.016, 10),
      new THREE.MeshBasicMaterial({ color: 0x2ee6d6 })
    );
    hubGlow.position.z = 0.009;
    wheelGroup.add(hubGlow);

    // Tilt wheel toward driver
    wheelGroup.rotation.x = -0.35;

    return wheelGroup;
  }

  private createExterior(): void {
    // Cyberpunk wedge: low gunmetal body, glowing light bars, big wing, underglow
    const bodyMat = paintedMetalMaterial(0x303846, 0.34);
    const darkerMat = darkMetalMaterial(0x10151e, 0.44);
    const glowRed = emissiveMaterial(0xff172e, 5.2);
    const glowPink = emissiveMaterial(0xff168c, 4.5);
    const glowTeal = emissiveMaterial(0x20eaf0, 4.2);

    // Low, wide main tub
    const body = new THREE.Mesh(trapezoidBox(1.78, 2.18, 0.48, 4.5), bodyMat);
    body.position.y = 0.22;
    body.castShadow = true;
    this.exteriorGroup.add(body);

    // Wedge nose: sloped plate from bumper up to the windshield base
    const nose = new THREE.Mesh(new THREE.BoxGeometry(1.95, 0.07, 1.7), bodyMat);
    nose.position.set(0, 0.72, -1.45);
    nose.rotation.x = 0.16;
    nose.castShadow = true;
    this.exteriorGroup.add(nose);

    // Wide front fenders
    for (const side of [-1, 1]) {
      const fender = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.3, 1.2), darkerMat);
      fender.position.set(side * 1.05, 0.55, -1.5);
      this.exteriorGroup.add(fender);
    }

    // Cabin set back, with raked windshield
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.42, 1.6), darkerMat);
    cabin.position.set(0, 1.02, 0.5);
    cabin.castShadow = true;
    this.exteriorGroup.add(cabin);

    const glass = new THREE.Mesh(
      new THREE.PlaneGeometry(1.5, 0.85),
      glassMaterial()
    );
    glass.position.set(0, 0.95, -0.35);
    glass.rotation.x = -0.55; // hard rake
    this.exteriorGroup.add(glass);
    this.exteriorGlass = glass;

    // Rear deck + wing on pylons
    const deck = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.16, 1.1), bodyMat);
    deck.position.set(0, 0.78, 1.7);
    this.exteriorGroup.add(deck);

    for (const side of [-1, 1]) {
      const pylon = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.34, 0.24), darkerMat);
      pylon.position.set(side * 0.75, 1.02, 2.05);
      this.exteriorGroup.add(pylon);
    }
    const wing = new THREE.Mesh(new THREE.BoxGeometry(2.15, 0.07, 0.5), bodyMat);
    wing.position.set(0, 1.22, 2.05);
    wing.castShadow = true;
    this.exteriorGroup.add(wing);
    const wingTip = new THREE.Mesh(new THREE.BoxGeometry(2.15, 0.08, 0.06), darkerMat);
    wingTip.position.set(0, 1.22, 2.28);
    this.exteriorGroup.add(wingTip);

    // Split front light bars (the reference's angry red glow)
    for (const side of [-1, 1]) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.09, 0.04), glowRed);
      bar.position.set(side * 0.52, 0.62, -2.26);
      this.exteriorGroup.add(bar);
      const lower = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.07, 0.04), glowRed);
      lower.position.set(side * 0.62, 0.42, -2.26);
      this.exteriorGroup.add(lower);
    }

    // Layered rear fascia: a dark physical mass with separated lamp bars is
    // much easier to read from the menu's low rear-three-quarter camera.
    const fascia = new THREE.Mesh(trapezoidBox(1.9, 2.16, 0.46, 0.22), darkerMat);
    fascia.position.set(0, 0.36, 2.27);
    this.exteriorGroup.add(fascia);
    const diffuser = new THREE.Mesh(trapezoidBox(1.86, 2.34, 0.22, 0.54), darkMetalMaterial(0x07090d, 0.72));
    diffuser.position.set(0, 0.11, 2.02);
    this.exteriorGroup.add(diffuser);
    for (const side of [-1, 1]) {
      const tail = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.095, 0.045), glowRed);
      tail.position.set(side * 0.55, 0.68, 2.37);
      this.exteriorGroup.add(tail);
      const lowerTail = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.07, 0.045), glowPink);
      lowerTail.position.set(side * 0.62, 0.48, 2.37);
      this.exteriorGroup.add(lowerTail);
    }

    // Small cyan wing-end markers retain the player color without turning
    // the whole spoiler into an unreadable luminous slab.
    for (const side of [-1, 1]) {
      const marker = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 0.08), glowTeal);
      marker.position.set(side * 1.02, 1.22, 2.28);
      this.exteriorGroup.add(marker);
    }

    // Side intakes + neon skirt strips
    for (const side of [-1, 1]) {
      const intake = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.22, 0.7), darkerMat);
      intake.position.set(side * 1.02, 0.55, 0.9);
      this.exteriorGroup.add(intake);

      const skirt = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.05, 3.4), glowTeal);
      skirt.position.set(side * 1.04, 0.22, 0);
      this.exteriorGroup.add(skirt);
    }

    // Underglow wash on the road
    const underglow = new THREE.Mesh(
      new THREE.PlaneGeometry(2.6, 4.9),
      new THREE.MeshBasicMaterial({
        color: 0xff2d95,
        transparent: true,
        opacity: 0.28,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    underglow.rotation.x = -Math.PI / 2;
    underglow.position.y = 0.03;
    this.exteriorGroup.add(underglow);

    // Low-profile wheels with teal hub discs
    const wheelMat = rubberMaterial();
    const positions = [
      [-0.88, 0.3, -1.45],
      [0.88, 0.3, -1.45],
      [-0.88, 0.3, 1.45],
      [0.88, 0.3, 1.45],
    ];
    for (const [x, y, z] of positions) {
      const tire = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.26, 16), wheelMat);
      tire.position.set(x, y, z);
      tire.rotation.z = Math.PI / 2;
      this.exteriorGroup.add(tire);

      const hub = new THREE.Mesh(new THREE.CircleGeometry(0.13, 8), glowTeal);
      hub.position.set(x + (x > 0 ? 0.14 : -0.14), y, z);
      hub.rotation.y = x > 0 ? Math.PI / 2 : -Math.PI / 2;
      this.exteriorGroup.add(hub);
    }
  }

  /**
   * 0 = pristine ... 4 = shattered. Each new tier adds crack clusters;
   * dropping back to 0 (new run) wipes the glass clean.
   */
  setWindshieldDamage(tier: number): void {
    if (!this.glassCtx || !this.glassTexture) return;
    const clamped = Math.max(0, Math.min(4, tier));
    if (clamped === this.damageTier) return;

    if (clamped < this.damageTier) {
      this.glassCtx.clearRect(0, 0, 1024, 400);
    } else {
      for (let t = this.damageTier; t < clamped; t++) {
        this.addCrackCluster(
          180 + Math.random() * 664,
          80 + Math.random() * 240,
          1 + t * 0.4
        );
      }
    }
    this.damageTier = clamped;
    this.glassTexture.needsUpdate = true;
  }

  private addCrackCluster(cx: number, cy: number, scale: number): void {
    const ctx = this.glassCtx!;
    const rays = 7 + Math.floor(Math.random() * 4);

    // impact core
    ctx.fillStyle = 'rgba(230,240,255,0.8)';
    ctx.beginPath();
    ctx.arc(cx, cy, 3.5 * scale, 0, Math.PI * 2);
    ctx.fill();

    // jagged radiating cracks
    for (let i = 0; i < rays; i++) {
      const angle = (i / rays) * Math.PI * 2 + Math.random() * 0.5;
      const length = (40 + Math.random() * 90) * scale;
      let x = cx;
      let y = cy;
      let a = angle;
      ctx.strokeStyle = `rgba(215,230,250,${0.28 + Math.random() * 0.3})`;
      ctx.lineWidth = 1 + Math.random() * 1.6;
      ctx.beginPath();
      ctx.moveTo(x, y);
      const segments = 4 + Math.floor(Math.random() * 3);
      for (let sgm = 0; sgm < segments; sgm++) {
        a += (Math.random() - 0.5) * 0.7;
        const step = length / segments;
        x += Math.cos(a) * step;
        y += Math.sin(a) * step * 0.7;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // faint stress rings
    ctx.strokeStyle = 'rgba(210,225,245,0.18)';
    ctx.lineWidth = 1;
    for (const r of [14, 26]) {
      ctx.beginPath();
      ctx.arc(cx, cy, r * scale, Math.random() * Math.PI, Math.random() * Math.PI + Math.PI * 1.2);
      ctx.stroke();
    }
  }

  /** Cockpit props only make sense from the driver's eyes — hide them for orbit/menu cameras.
   *  The exterior windshield is the inverse: shown outside, hidden in first person. */
  setCockpitVisible(visible: boolean): void {
    this.hoodGroup.visible = visible;
    this.steeringWheel.visible = visible;
    this.exteriorGroup.visible = !visible;
    if (this.exteriorGlass) this.exteriorGlass.visible = !visible;
  }

  setSteeringInput(lateral: number): void {
    // Smooth the steering wheel rotation
    const targetAngle = -lateral * Math.PI * 0.5; // half turn max
    this.steeringAngle += (targetAngle - this.steeringAngle) * 0.2;
    this.steeringWheel.rotation.z = this.steeringAngle;
  }

  /** Position the car on the rail; steering adds a subtle body roll. */
  setPose(railPath: RailPath, t: number, lateral: number, steering: number): void {
    const { position, quaternion } = railPath.getTransform(t, lateral);
    this.mesh.position.copy(position);

    const targetBank = -steering * 0.055;
    this.bankAngle += (targetBank - this.bankAngle) * 0.12;
    this.rollQuat.setFromAxisAngle(this.forwardAxis, this.bankAngle);
    this.mesh.quaternion.copy(quaternion).multiply(this.rollQuat);
  }
}

/** Low-poly automotive wedge with a wider rear and properly angled side faces. */
function trapezoidBox(widthFront: number, widthBack: number, height: number, depth: number): THREE.BufferGeometry {
  const front = widthFront * 0.5;
  const back = widthBack * 0.5;
  const zFront = -depth * 0.5;
  const zBack = depth * 0.5;
  const positions = new Float32Array([
    -front, 0, zFront, front, 0, zFront, front, height, zFront, -front, height, zFront,
    -back, 0, zBack, back, 0, zBack, back, height, zBack, -back, height, zBack,
  ]);
  const indices = [
    0, 1, 2, 0, 2, 3,
    5, 4, 7, 5, 7, 6,
    4, 0, 3, 4, 3, 7,
    1, 5, 6, 1, 6, 2,
    3, 2, 6, 3, 6, 7,
    4, 5, 1, 4, 1, 0,
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}
